import React from 'react';
import {
  Layout,
  Typography,
  Space,
  Button,
  Badge,
  Avatar,
  Divider,
  Tooltip,
  Input,
} from 'antd';
import {
  MenuOutlined,
  BellOutlined,
  SettingOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useAdmin } from '../context/AdminContext';

const { Header } = Layout;
const { Title, Text } = Typography;

interface AdminHeaderProps {
  title: string;
  description?: string;
  onMenuClick?: () => void;
}

export const AdminHeader: React.FC<AdminHeaderProps> = ({
  title,
  description,
  onMenuClick,
}) => {
  const { t } = useTranslation();
  const { user } = useAdmin();

  return (
    <Header className="admin-header">
      {/* Left — Menu toggle + Page title */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          flex: '0 0 auto',
          minWidth: 0,
        }}
      >
        <Tooltip title={t('admin.header.toggle_sidebar')}>
          <Button
            type="text"
            icon={<MenuOutlined />}
            onClick={onMenuClick}
            className="admin-header-btn"
          />
        </Tooltip>

        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <Title
            level={5}
            style={{
              margin: 0,
              color: 'var(--admin-text-primary)',
              fontSize: 15,
              fontWeight: 700,
              lineHeight: '20px',
              whiteSpace: 'nowrap',
              letterSpacing: '-0.2px',
            }}
          >
            {title}
          </Title>
          {description && (
            <Text
              style={{
                fontSize: 11.5,
                color: 'var(--admin-text-secondary)',
                lineHeight: '16px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: 280,
              }}
            >
              {description}
            </Text>
          )}
        </div>
      </div>

      {/* Center — Global search */}
      <div
        className="admin-header-search"
        style={{ flex: '1 1 auto', maxWidth: 380, margin: '0 20px' }}
      >
        <Input
          prefix={
            <SearchOutlined style={{ color: 'var(--admin-text-secondary)', fontSize: 14 }} />
          }
          placeholder={t('admin.header.search_placeholder')}
        />
      </div>

      {/* Right — Actions + User profile */}
      <Space size={6} style={{ flex: '0 0 auto' }}>
        <Tooltip title={t('admin.header.notifications')}>
          <Badge count={0} size="small" offset={[-2, 2]}>
            <Button
              type="text"
              icon={<BellOutlined />}
              className="admin-header-btn"
            />
          </Badge>
        </Tooltip>

        <Tooltip title={t('admin.header.settings')}>
          <Button
            type="text"
            icon={<SettingOutlined />}
            className="admin-header-btn"
          />
        </Tooltip>

        <Divider type="vertical" style={{ height: 22, margin: '0 6px' }} />

        {/* User chip */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 9,
            padding: '5px 10px 5px 5px',
            borderRadius: 10,
            border: '1.5px solid transparent',
          }}
        >
          <Avatar
            size={32}
            style={{
              background: 'var(--admin-primary)',
              fontWeight: 700,
              fontSize: 13,
              flexShrink: 0,
            }}
          >
            {user?.firstName?.[0]?.toUpperCase() || 'A'}
          </Avatar>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <span
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: 'var(--admin-text-primary)',
                lineHeight: '18px',
                whiteSpace: 'nowrap',
              }}
            >
              {user ? `${user.firstName} ${user.lastName}` : 'Admin'}
            </span>
            <span
              style={{
                fontSize: 10.5,
                color: 'var(--admin-primary)',
                fontWeight: 700,
                lineHeight: '14px',
                letterSpacing: '0.4px',
              }}
            >
              {user?.role || 'ADMIN'}
            </span>
          </div>
        </div>
      </Space>
    </Header>
  );
};

export default AdminHeader;
